<template>
    <div>
        <table class="table table-striped table-class customer-highlight-table hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headers"
                        :key="idx"
                        @click="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ header.label }}
                    </th>
                    <th scope="col" class="dashboard-sort-header">ACT BTN</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(item, idx) in paginatedData"
                    :key="rowKey(item, idx)"
                    :class="rowClass(item)"
                >
                    <td>
                        <PartNumberDisplay
                            :part-number="item.part_number"
                            :selected-part="part_selected"
                        />
                    </td>
                    <td>{{ item.description }}</td>
                    <td>{{ formatComponentControlDate(item.entry_date) }}</td>
                    <td>{{ formatComponentControlDate(item.next_delivery_date) }}</td>
                    <td>{{ formatNumeric(item.unit_cost, 'money') }}</td>
                    <td>{{ formatNumeric(item.total_cost, 'money') }}</td>
                    <td>{{ formatNumeric(item.qty_ordered) }}</td>
                    <td>{{ item.vendor_name }}</td>
                    <td>{{ formatNumeric(item.poso_qty) }}</td>
                    <td>
                        <a
                            href="javascript:;"
                            @click="triggerMoreDetails(item)"
                            class="mx-2 dashboard-action-link"
                            title="More details"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia'
    import { formatComponentControlDate } from '@/helpers/dates.js'
    import { formatNumeric, normalizePart, parseNumeric } from '@/helpers/string.js'
    import { buildDashboardRowHighlightClass } from '@/helpers/dashboardRowHighlight.js'
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    export default {
        name: 'OpenPurchaseOrdersTable',
        components: {
            PartNumberDisplay,
        },
        props: {
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
            data: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                headers: [
                    { label: 'Part Number', type: 'string' },
                    { label: 'Description', type: 'string' },
                    { label: 'Entry Date', type: 'date' },
                    { label: 'Next Delivery Date', type: 'date' },
                    { label: 'Unit Cost', type: 'numeric' },
                    { label: 'Total Cost', type: 'numeric' },
                    { label: 'Qty Ordered', type: 'numeric' },
                    { label: 'Vendor', type: 'string' },
                    { label: 'POSO Qty', type: 'numeric' },
                ],
                sortColumn: 2,
                columnType: 'date',
                sortDirection: 'desc',
                currentPage: 1,
                itemsPerPage: 10,
            }
        },
        computed: {
            ...mapState(useSubstitutesStore, ['selectedSubstituteOnModal']),
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = a[key]
                    let valueB = b[key]

                    if (this.columnType === 'numeric') {
                        valueA = parseNumeric(valueA, 0)
                        valueB = parseNumeric(valueB, 0)
                    } else if (this.columnType === 'date') {
                        valueA = formatComponentControlDate(valueA)
                        valueB = formatComponentControlDate(valueB)
                    } else {
                        valueA = String(valueA).toLowerCase()
                        valueB = String(valueB).toLowerCase()
                    }

                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.ceil(this.data.length / this.itemsPerPage)
            },
        },
        watch: {
            data: {
                handler() {
                    this.applyDefaultSort()
                },
                immediate: true,
            },
        },
        methods: {
            formatComponentControlDate,
            formatNumeric,
            applyDefaultSort() {
                this.sortColumn = 2
                this.columnType = 'date'
                this.sortDirection = 'desc'
                this.currentPage = 1
            },
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                const keys = [
                    'part_number',
                    'description',
                    'entry_date',
                    'next_delivery_date',
                    'unit_cost',
                    'total_cost',
                    'qty_ordered',
                    'vendor_name',
                    'poso_qty',
                ]
                return keys[index]
            },
            triggerMoreDetails(details) {
                this.$emit('more-details', details)
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            isActivePart(partNumber) {
                if (!this.selectedSubstituteOnModal || partNumber == null) return false
                const selected = normalizePart(this.selectedSubstituteOnModal)
                const current = normalizePart(partNumber)
                return selected.length > 0 && selected === current
            },
            rowClass(item) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    selectedPart: this.part_selected,
                    partNumber: item?.part_number,
                    activePart: this.isActivePart(item.part_number),
                })
            },
            rowKey(item, index) {
                if (item && item.pod_auto_key != null) {
                    return `opo-${item.pod_auto_key}`
                }
                const parts = [
                    item?.part_number,
                    item?.po_number,
                    item?.entry_date,
                    item?.next_delivery_date,
                    item?.unit_cost,
                    item?.qty_ordered,
                    index,
                ]
                return `opo-${parts.filter((value) => value != null).join('-')}`
            },
        },
    }
</script>

<style scoped>
    .table thead td,
    .table thead th {
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
    }
</style>
